<!--
  Top-level layout. Three tabs share a sidebar of cohort filters plus a
  main pane of chart cards. Each chart card is independently collapsible
  with its own controls and "Learn more" panel.
-->
<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, watch } from 'vue';
import type { CohortFilters } from './api';
import UnitToggle from './components/UnitToggle.vue';
import BarbellIcon from './components/BarbellIcon.vue';
// Lazy-load each view so Chart.js (~140KB gzipped) only loads with views
// that actually render charts (Trends/Career/Analysis/Lifter), not on
// content-only pages (Home/About/Donate/ReleaseNotes). Vite emits a
// per-view chunk; the active view's chunk fetches in parallel with the
// initial paint and on tab switch.
const HomeView         = defineAsyncComponent(() => import('./views/HomeView.vue'));
const TrendsView       = defineAsyncComponent(() => import('./views/TrendsView.vue'));
const CareerView       = defineAsyncComponent(() => import('./views/CareerView.vue'));
const AnalysisV2View   = defineAsyncComponent(() => import('./views/AnalysisV2View.vue'));
const LifterView       = defineAsyncComponent(() => import('./views/LifterView.vue'));
const AboutView        = defineAsyncComponent(() => import('./views/AboutView.vue'));
const DonateView       = defineAsyncComponent(() => import('./views/DonateView.vue'));
const MeetsView        = defineAsyncComponent(() => import('./views/MeetsView.vue'));
const MilestonesView   = defineAsyncComponent(() => import('./views/MilestonesView.vue'));
const ReleaseNotesView = defineAsyncComponent(() => import('./views/ReleaseNotesView.vue'));
const PredictionsView   = defineAsyncComponent(() => import('./views/PredictionsView.vue'));
const CalculatorsView   = defineAsyncComponent(() => import('./views/CalculatorsView.vue'));
import { api } from './api';
import { pageview } from './analytics';

// `predictions` is intentionally NOT in any nav — it's an internal
// model-accuracy retrospective, reachable only by direct URL. `donate`
// is now surfaced (header "♥ Support", drawer, and footer). All tabs
// stay in TABS regardless so the routing and SEO machinery treat them
// like any other tab.
const TABS = ['trends', 'career', 'analysis', 'lifter', 'home', 'about', 'donate', 'meets', 'milestones', 'release-notes', 'predictions', 'calculators'] as const;
type TabId = typeof TABS[number];

/** Tabs that show the cohort filter sidebar. Lifter / home / about don't
 *  need it (lifter has its own search UX; home/about are static content).
 *  Career is a curated Raw + Wraps journey with its own in-page sex toggle,
 *  so it deliberately doesn't take the cohort sidebar. */

// Cohort filter keys that round-trip through the URL query string. Stays
// in sync with the CohortFilters interface in api.ts. Anything not on this
// list is treated as ephemeral and won't be in the shareable URL.
const URL_FILTER_KEYS: (keyof CohortFilters)[] = [
  'event', 'equipment', 'sex', 'weightClass', 'ageClass', 'country', 'state',
  'division', 'tested', 'federation', 'parentFederation', 'year', 'from', 'to',
  'meetNumber', 'maxMeetNumber', 'minMeetsInCareer', 'debutOnly',
  'debutAge', 'minDebutAge', 'maxDebutAge', 'activeOnly', 'minYearsActive',
];

// What a fresh visitor sees. Only DIFFERENCES from this go into the URL —
// so /analysis is clean by default, and only deviations make the bar long.
// When parsing a URL, missing keys fall back to these defaults; an explicit
// empty string in the URL is treated as "cleared" (e.g. `?event=` clears the
// otherwise-default event filter).
// Defaults are intentionally inclusive on Sex — initial charts should
// represent the full population. SBD + Raw stay defaulted because they're
// the most common slices and avoiding them would mix events/equipment
// (apples-to-oranges) on first paint, but Sex is genuinely a "filter to
// narrow" rather than a normalization.
const DEFAULT_FILTERS: CohortFilters = { event: 'SBD', equipment: 'Raw' };

function parseTabFromPath(path: string): TabId {
  const seg = path.replace(/^\/+|\/+$/g, '').split('/')[0];
  // The v2 preview lived at /analysis-v2 before it was promoted to the
  // default /analysis. Redirect any lingering links so they don't 404-to-home.
  if (seg === 'analysis-v2') return 'analysis';
  if ((TABS as readonly string[]).includes(seg)) return seg as TabId;
  // Empty/unknown paths land on the home page. Anyone hitting `/home`
  // also resolves to 'home' here, but buildUrl normalizes that tab to
  // `/` so the URL bar collapses on the next replaceState.
  return 'home';
}

function parseFiltersFromUrl(search: string): CohortFilters {
  const params = new URLSearchParams(search);
  const result: CohortFilters = { ...DEFAULT_FILTERS };
  for (const k of URL_FILTER_KEYS) {
    if (params.has(k as string)) {
      // Present in URL — use its value, even if empty (= explicit clear).
      (result as any)[k] = params.get(k as string) ?? '';
    }
    // Absent from URL — keep the default (or undefined if no default).
  }
  return result;
}

function buildUrl(tab: TabId, filters: CohortFilters): string {
  // Home is the root URL — `/`. About lives at `/about`. Neither carries
  // any query string; filters are meaningless on static content pages.
  if (tab === 'home')   return '/';
  if (tab === 'about')  return '/about';
  if (tab === 'donate') return '/donate';
  // MeetsView owns a `/meets/<id>` detail path. buildUrl is App-level and
  // filter-aware only, so preserve that segment rather than collapsing it —
  // otherwise a filter change or the canonical-link watcher would rewrite a
  // meet detail URL back to the bare list.
  if (tab === 'meets')  return /^\/meets\/\d+\/?$/.test(window.location.pathname)
    ? window.location.pathname
    : '/meets';
  if (tab === 'milestones') return '/milestones';
  if (tab === 'predictions') return '/predictions';
  if (tab === 'calculators') return '/calculators';
  const params = new URLSearchParams();
  for (const k of URL_FILTER_KEYS) {
    const v    = (filters as any)[k];
    const dflt = (DEFAULT_FILTERS as any)[k];
    // Treat undefined and empty-string as equivalent for comparison purposes —
    // both mean "no filter set". Skips writing when the current value matches
    // the default.
    const cur  = v ?? '';
    const def  = dflt ?? '';
    if (cur === def) continue;
    params.set(k as string, String(v ?? ''));
  }
  const q = params.toString();
  return `/${tab}${q ? '?' + q : ''}`;
}

// Initial filters: parse the URL query string (shared link), else defaults.
// localStorage was previously used as a fallback for "where I left off"
// across sessions, but it confused expectations — users wanted clicking a
// tab to feel like a clean slate. URL is now the only persistence layer.
const filters = ref<CohortFilters>(parseFiltersFromUrl(window.location.search));
const tab     = ref<TabId>(parseTabFromPath(window.location.pathname));

function goRoot() {
  // Logo click → home page at `/`.
  suppressUrlSync = true;
  tab.value = 'home';
  filters.value = { ...DEFAULT_FILTERS };
  window.history.pushState({}, '', '/');
  queueMicrotask(() => { suppressUrlSync = false; });
}

/**
 * Header tab click. Special-cases the lifter tab: clicking it while
 * already on a lifter's detail page should reset to the search view
 * (clear ?id= and re-trigger the popstate handler so LifterView drops
 * its selectedId). Without this the click was a no-op since
 * `tab.value === 'lifter'` already.
 */
function navigateToTab(target: TabId) {
  const goingToLifter = target === 'lifter';
  const onLifterDetail = goingToLifter
    && tab.value === 'lifter'
    && new URLSearchParams(window.location.search).has('id');

  if (onLifterDetail) {
    // pushState changes the URL but does NOT fire a popstate event,
    // so LifterView's own popstate listener (which watches ?id=
    // and clears selectedId when missing) wouldn't run. Dispatch a
    // synthetic popstate so every listener (App + LifterView) sees
    // the new URL state.
    window.history.pushState({}, '', '/lifter');
    window.dispatchEvent(new PopStateEvent('popstate'));
    return;
  }

  tab.value = target;
}

// Jump straight to a lifter's profile (from the home-page lookup). Push
// /lifter?id=N and fire a synthetic popstate so onPopState switches the tab
// and LifterView reads the id — same mechanism as a back/forward nav.
function openLifter(id: number) {
  window.history.pushState({}, '', '/lifter?id=' + id);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// Deep-link from the home page to a specific section on another tab (e.g. the
// "is the sport dying?" card → Trends #boom). Switch tabs, then poll for the
// anchor — the target view is a lazily-loaded chunk, so its DOM may not exist
// for a few frames — and smooth-scroll to it once it mounts. The hashchange
// event lets anchor-aware components (ChartCard) auto-open if they match.
function openSection(payload: { tab: TabId; hash?: string }) {
  navigateToTab(payload.tab);
  const hash = payload.hash;
  if (!hash) return;
  let tries = 0;
  const tryScroll = () => {
    const el = document.getElementById(hash);
    if (el) {
      window.history.replaceState({}, '', `/${payload.tab}#${hash}`);
      window.dispatchEvent(new Event('hashchange'));
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (tries++ < 60) {
      setTimeout(tryScroll, 60); // ~3.6s max while the chunk loads
    }
  };
  setTimeout(tryScroll, 60);
}

// Bumped only by legacy filter-apply paths; the current views watch their own
// v-model:filters, so it stays 0 but is still passed as the :tick prop.
const applyTick = ref(0);

// Mobile nav drawer state. Hamburger opens a full-height overlay
// that lists tabs vertically with comfortable touch targets — much
// better than the wrapping flex-row of small buttons we had before.
const mobileMenuOpen = ref(false);
function toggleMobileMenu() { mobileMenuOpen.value = !mobileMenuOpen.value; }
function closeMobileMenu() { mobileMenuOpen.value = false; }
// Auto-close drawer on tab change (so navigating from inside the
// drawer feels like a normal app, not a stuck overlay).
watch(tab, () => { mobileMenuOpen.value = false; });

/**
 * Per-route SEO. Each tab gets its own title + meta description so search
 * engine result snippets can target different keywords (career trajectory
 * vs regression analysis vs lifter lookup), and so social shares of a
 * specific tab carry the right preview.
 *
 * The static title in index.html is the fallback; this watcher mutates
 * <title> and the `description` meta tag whenever tab changes — including
 * the initial paint, hence `immediate: true`.
 */
const ROUTE_SEO: Record<TabId, { title: string; description: string }> = {
  home: {
    title: 'Powerlifting Trends | Data, Statistics & Career Analysis for Powerlifters',
    description: 'Powerlifting analytics built on every meet from the OpenPowerlifting dataset. Year-over-year participation trends, regression-grade statistical analysis, career trajectory benchmarks, and meet-by-meet percentile rankings. Free and ad-free.',
  },
  trends: {
    title: 'Powerlifting Trends | Year-over-Year Participation & Strength Data',
    description: 'Plot powerlifting participation, average Dots, and lift-specific trends year-over-year across any cohort. Group by weight class, equipment, federation, sex, or age class.',
  },
  career: {
    title: 'Powerlifting Career Arc | Typical Trajectories & Retention Curves',
    description: 'See what a typical powerlifting career looks like meet by meet. Average Dots curves from debut through meet 30+ with p25/p75 bands, plus retention rates at meet 5 and 10.',
  },
  analysis: {
    title: 'Powerlifting Analysis | Regression Statistics & Effect-Size Verdicts',
    description: 'Linear regression analysis over 3.9M powerlifting entries. Tests how participation, debut age, career length, and other factors predict strength outcomes, with plain-English verdicts that gate on effect size rather than p-value alone.',
  },
  lifter: {
    title: 'Powerlifting Lifter Lookup | Search, Compare & Rank Any Career',
    description: 'Look up any powerlifter\'s career and benchmark every meet against the cohort that compares. Percentile rankings by Dots within event, equipment, weight class, and division.',
  },
  about: {
    title: 'About Powerlifting Trends | Mission, Methodology & Data Sources',
    description: 'Why we built Powerlifting Trends, where the data comes from, and how the regressions are calibrated.',
  },
  donate: {
    title: 'Support Powerlifting Trends',
    description: 'The site is free, ad-free, and will stay that way. Donations help cover hosting and development costs that come out of the maintainer\'s pocket.',
  },
  meets: {
    title: 'Powerlifting Meets | Search OpenPowerlifting Meet Database',
    description: 'Browse and search every meet in the OpenPowerlifting database. Filter by federation, year, and name; click any meet for the full roster and results.',
  },
  milestones: {
    title: 'Powerlifting Milestones | A Timeline of Firsts',
    description: 'The first ever 500kg total. The first 1000kg raw. The first woman to deadlift 250kg. A scrolling timeline of historic firsts in powerlifting.',
  },
  'release-notes': {
    title: 'Powerlifting Trends | Release Notes',
    description: 'What\'s new on Powerlifting Trends. Recent feature ships, model updates, and notable bug fixes, newest first.',
  },
  predictions: {
    title: 'Prediction Accuracy | Internal',
    description: 'Internal model-accuracy retrospective: stored forecasts graded against realized totals.',
  },
  calculators: {
    title: 'Powerlifting Calculators | Strength Percentile & Meet Placement',
    description: 'Free powerlifting calculators built on the OpenPowerlifting dataset. See what percentile your squat, bench, deadlift, total, and Dots rank in your weight class and age group, and estimate how you\'d place at a meet, with 1RM and attempt planning.',
  },
};

// Pages that aren't in any nav and shouldn't show up in search results
// until they're publicly launched. Set their <meta name="robots"> to
// noindex on entry; restore "index, follow" on exit (so a user who lands
// on /donate first and clicks back into the app isn't stuck noindex'd).
const NOINDEX_TABS = new Set<TabId>(['predictions']);

// Upsert a <meta> tag by name/property so social-share previews stay in
// step with the route (a shared /career link shouldn't preview homepage copy).
function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function applyRouteSeo(t: TabId) {
  const meta = ROUTE_SEO[t];
  if (!meta) return;
  document.title = meta.title;
  let descTag = document.querySelector('meta[name="description"]');
  if (!descTag) {
    descTag = document.createElement('meta');
    descTag.setAttribute('name', 'description');
    document.head.appendChild(descTag);
  }
  descTag.setAttribute('content', meta.description);
  let robotsTag = document.querySelector('meta[name="robots"]');
  if (!robotsTag) {
    robotsTag = document.createElement('meta');
    robotsTag.setAttribute('name', 'robots');
    document.head.appendChild(robotsTag);
  }
  robotsTag.setAttribute('content', NOINDEX_TABS.has(t) ? 'noindex, nofollow' : 'index, follow');
  // Canonical follows the URL the user is on so search engines don't
  // index two URLs for the same content (e.g. /trends?event=SBD vs /trends).
  const canonical = `https://powerliftingtrends.com${buildUrl(t, { ...DEFAULT_FILTERS })}`;
  let canonicalTag = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!canonicalTag) {
    canonicalTag = document.createElement('link');
    canonicalTag.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalTag);
  }
  canonicalTag.href = canonical;
  // Route-specific Open Graph + Twitter card (image stays the static default).
  upsertMeta('property', 'og:title', meta.title);
  upsertMeta('property', 'og:description', meta.description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('name', 'twitter:title', meta.title);
  upsertMeta('name', 'twitter:description', meta.description);
}

watch(tab, applyRouteSeo, { immediate: true });

// SPA pageview tracking for GA4. The gtag snippet in index.html runs
// with `send_page_view: false`, so we fire one manually on every tab
// change AND on initial paint (immediate: true). Path is whatever
// buildUrl produces for the current state, so query-string filters
// show up in the page_path dimension in GA.
watch(
  () => tab.value,
  () => {
    // Defer one tick so the SEO watcher above has updated document.title
    // before we read it for the page_view payload.
    queueMicrotask(() => {
      pageview(buildUrl(tab.value, filters.value));
    });
  },
  { immediate: true },
);

// Track whether a URL change is being driven by the browser (popstate) so
// our watchers don't echo it back into the history stack.
let suppressUrlSync = false;

// Filters change → update URL in place (no new history entry; the user is
// just refining their current view). Preserve the hash so deep-links
// to specific cards (e.g. /analysis#2nd-vs-3rd-pr-strategy) stay
// linkable when the user fiddles with sidebar filters.
watch(filters, () => {
  if (suppressUrlSync) return;
  window.history.replaceState({}, '', buildUrl(tab.value, filters.value) + window.location.hash);
}, { deep: true });

// Tab click → reset filters to defaults and push a clean URL with no query
// string. Treats tab navigation as "fresh start" rather than carrying over
// state from a previous tab — fewer surprises like "I switched to Analysis
// and somehow my Trends filters are still here".
watch(tab, (newTab, oldTab) => {
  if (suppressUrlSync) return;
  if (newTab === oldTab) return;
  // Suppress the filters-watcher echo while we mutate both at once.
  suppressUrlSync = true;
  const nextFilters = { ...DEFAULT_FILTERS };
  filters.value = nextFilters;
  // buildUrl special-cases home (→ "/") and about (→ "/about") so we
  // don't have to special-case them here too.
  window.history.pushState({}, '', buildUrl(newTab, nextFilters));
  queueMicrotask(() => { suppressUrlSync = false; });
});

// Browser back/forward — re-read both tab and filters from the URL.
function onPopState() {
  suppressUrlSync = true;
  tab.value     = parseTabFromPath(window.location.pathname);
  filters.value = parseFiltersFromUrl(window.location.search);
  // Allow Vue's reactivity to flush before we re-enable URL syncing,
  // otherwise the watchers above would push another history entry.
  queueMicrotask(() => { suppressUrlSync = false; });
}

// Data freshness label for the footer. Single /meta call on mount; the
// response is CloudFront-cached so this is essentially free.
const dataThrough = ref<string | null>(null);
onMounted(async () => {
  try {
    const m = await api.meta();
    const d = m.data_freshness?.latest_meet_date;
    if (d) {
      dataThrough.value = new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
      });
    }
  } catch {
    // Quietly skip — footer just won't show the date.
  }
});

onMounted(() => {
  // Normalize the initial URL — if the user landed on `/` we still want
  // `/trends?...` in the bar so they can copy a clean shareable link.
  //
  // Exception: views that own their own `?id=` query string keep it
  // through the bootstrap normalization. Stripping it would mean a
  // refresh / direct-link of `/lifter?id=N` (or `/meets?id=N`) lands on
  // the search page instead of the detail view. buildUrl is App-level
  // filter-aware only; it doesn't know about view-local URL state, so
  // we leave the search verbatim when the route is one of the views
  // that scopes its own ?id=.
  const search = window.location.search;
  const hash = window.location.hash;  // preserve deep-link card anchor (e.g. #2nd-vs-3rd-pr-strategy)
  const idScopedTab = (tab.value === 'lifter' || tab.value === 'meets')
    && new URLSearchParams(search).has('id');
  // `/meets/<id>` carries its id in the path, so it is id-scoped without a
  // `?id=`; buildUrl already preserves it, but the query string (view/event/
  // equip/... that MeetsView owns) has to survive normalization too.
  const pathScopedMeet = /^\/meets\/\d+\/?$/.test(window.location.pathname);
  const normalized = idScopedTab
    ? `/${tab.value}${search}`
    : pathScopedMeet
      ? `${window.location.pathname}${search}`
      : buildUrl(tab.value, filters.value);
  window.history.replaceState({}, '', normalized + hash);
  window.addEventListener('popstate', onPopState);
});
onUnmounted(() => window.removeEventListener('popstate', onPopState));
</script>

<template>
  <div class="layout">
    <!-- Keyboard / screen-reader affordance: lets users jump past the
         8-item nav straight to page content. Visually hidden until it
         receives focus (first Tab press), then pins to the top-left. -->
    <a href="#main-content" class="skip-link">Skip to main content</a>
    <header>
      <a href="/" class="brand" @click.prevent="goRoot" aria-label="Powerlifting Trends — home">
        <img src="/logo.svg" alt="" class="logo-img" />
        <h1>Powerlifting <span class="brand-trends">Trends</span></h1>
      </a>
      <nav class="nav-desktop">
        <button :class="{ active: tab === 'trends' }"   @click="navigateToTab('trends')">Trends</button>
        <button :class="{ active: tab === 'career' }"   @click="navigateToTab('career')">Career Arc</button>
        <button :class="{ active: tab === 'analysis' }" @click="navigateToTab('analysis')">Analysis</button>
        <button :class="{ active: tab === 'lifter' }"   @click="navigateToTab('lifter')">Lifters</button>
        <button :class="{ active: tab === 'meets' }"    @click="navigateToTab('meets')">Meets</button>
        <button :class="{ active: tab === 'calculators' }" @click="navigateToTab('calculators')">Calculators</button>
        <button :class="{ active: tab === 'milestones' }" @click="navigateToTab('milestones')">Milestones</button>
        <button :class="{ active: tab === 'about' }"    @click="navigateToTab('about')">About</button>
        <button class="nav-support" :class="{ active: tab === 'donate' }" @click="navigateToTab('donate')"><BarbellIcon /> Support</button>
      </nav>
      <UnitToggle class="header-toggle" />
      <!-- Hamburger trigger only renders on mobile; on desktop the nav
           lives inline in the header and this is hidden via @media. -->
      <button
        class="nav-hamburger"
        :class="{ open: mobileMenuOpen }"
        :aria-expanded="mobileMenuOpen"
        aria-label="Toggle navigation"
        @click="toggleMobileMenu"
      >
        <span></span><span></span><span></span>
      </button>
    </header>

    <!-- Mobile drawer — slides down from the header. Click backdrop or
         a nav button to close. -->
    <div v-if="mobileMenuOpen" class="nav-drawer-backdrop" @click="closeMobileMenu"></div>
    <nav class="nav-drawer" :class="{ open: mobileMenuOpen }" :aria-hidden="!mobileMenuOpen">
      <button :class="{ active: tab === 'trends' }"   @click="navigateToTab('trends')">Trends</button>
      <button :class="{ active: tab === 'career' }"   @click="navigateToTab('career')">Career Arc</button>
      <button :class="{ active: tab === 'analysis' }" @click="navigateToTab('analysis')">Analysis</button>
      <button :class="{ active: tab === 'lifter' }"   @click="navigateToTab('lifter')">Lifters</button>
      <button :class="{ active: tab === 'meets' }"    @click="navigateToTab('meets')">Meets</button>
      <button :class="{ active: tab === 'calculators' }" @click="navigateToTab('calculators')">Calculators</button>
      <button :class="{ active: tab === 'milestones' }" @click="navigateToTab('milestones')">Milestones</button>
      <button :class="{ active: tab === 'about' }"    @click="navigateToTab('about')">About</button>
      <button class="nav-support" :class="{ active: tab === 'donate' }" @click="navigateToTab('donate')"><BarbellIcon /> Support</button>
    </nav>

    <div class="body no-aside">
      <main id="main-content" tabindex="-1">
        <TrendsView   v-if="tab === 'trends'" />
        <CareerView   v-else-if="tab === 'career'" />
        <AnalysisV2View v-else-if="tab === 'analysis'" v-model:filters="filters" :tick="applyTick" />
        <LifterView         v-else-if="tab === 'lifter'" v-model:filters="filters" :tick="applyTick" />
        <HomeView           v-else-if="tab === 'home'" @navigate="navigateToTab" @open-lifter="openLifter" @open-section="openSection" />
        <AboutView          v-else-if="tab === 'about'" />
        <DonateView         v-else-if="tab === 'donate'" />
        <MeetsView          v-else-if="tab === 'meets'" />
        <MilestonesView     v-else-if="tab === 'milestones'" />
        <ReleaseNotesView   v-else-if="tab === 'release-notes'" />
        <PredictionsView    v-else-if="tab === 'predictions'" />
        <CalculatorsView    v-else-if="tab === 'calculators'" />
      </main>
    </div>

    <footer>
      <div class="footer-inner">
        <span>© 2026 Powerlifting Trends</span>
        <span class="sep">·</span>
        <span>Data from <a href="https://www.openpowerlifting.org" target="_blank" rel="noopener">OpenPowerlifting</a></span>
        <span v-if="dataThrough" class="sep">·</span>
        <span v-if="dataThrough" class="data-through">Data through {{ dataThrough }}</span>
        <span class="sep">·</span>
        <a href="/release-notes" @click.prevent="navigateToTab('release-notes')">Release Notes</a>
        <span class="sep">·</span>
        <a href="/donate" class="footer-support" @click.prevent="navigateToTab('donate')"><BarbellIcon /> Support the site</a>
      </div>
    </footer>
  </div>
</template>

<style scoped>
.layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Subtle global atmosphere — a faint orange spotlight in the top-right
   plus a near-imperceptible noise texture. Sits behind everything so
   the slate canvas reads layered, not flat. The radial gradient is
   tuned so it's barely there — a designer would call it "presence,
   not decoration." Pointer-events:none keeps it click-through. */
.layout::before {
  content: '';
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: -1;
  background:
    radial-gradient(1100px 540px at 90% -8%, rgba(247, 129, 102, 0.07), transparent 60%),
    radial-gradient(900px 600px at -10% 110%, rgba(196, 240, 74, 0.04), transparent 60%);
}

/* Skip link — off-screen until focused, then anchored top-left above
   everything. Standard a11y pattern so keyboard users don't have to tab
   through the whole nav on every page. */
.skip-link {
  position: fixed;
  top: 8px;
  left: 8px;
  z-index: 2000;
  padding: 10px 16px;
  background: var(--accent);
  color: #0d1014;
  font-size: 13px;
  font-weight: 600;
  border-radius: var(--radius-md);
  text-decoration: none;
  transform: translateY(-160%);
  transition: transform 0.16s ease;
}
.skip-link:focus { transform: none; outline: 2px solid var(--accent-2); outline-offset: 2px; }

header {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 14px 24px;
  border-bottom: 1px solid var(--border);
  background: var(--bg-elev);
  /* Sticky so the nav is always reachable on the long-scroll data pages
     (Trends / Career / Analysis run 3-6 screens). Pairs with
     scroll-padding-top in style.css so anchor jumps clear the bar.
     z-index sits below the mobile drawers (99-1100) but above page
     content. */
  position: sticky;
  top: 0;
  z-index: 50;
  box-shadow: 0 6px 20px -12px rgba(0, 0, 0, 0.55);
}
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  border-radius: 6px;
  padding: 2px 4px;
  margin: -2px -4px;
}
.brand:hover h1 { color: var(--accent); }
.brand:focus-visible { outline: 2px solid var(--accent-2); outline-offset: 2px; }
.brand .logo-img { width: 24px; height: 24px; display: block; }
/* Wordmark in the display font — italic Instrument Serif. "Trends"
   inherits the brand orange so the verbal identity carries the same
   color split as the plate-bar mark next to it. */
.brand h1 {
  margin: 0;
  font-family: var(--font-display);
  font-style: italic;
  font-weight: 400;
  font-size: 19px;
  letter-spacing: -0.005em;
  transition: color 0.12s ease;
}
.brand h1 .brand-trends {
  color: var(--accent);
}
/* Override the .brand:hover rule below so hovering the brand link
   doesn't double-color the orange "Trends" — keep it stable so the
   color split is the constant identity, not a reactive flicker. */
.brand:hover h1 .brand-trends { color: var(--accent); }

.nav-desktop { display: flex; gap: 2px; margin-left: 12px; }
/* Push the unit toggle to the far right on desktop. */
.header-toggle { margin-left: auto; }
.nav-desktop button {
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--text-dim);
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  position: relative;
  transition: color 0.15s ease;
}
.nav-desktop button:hover { color: var(--text); }
.nav-desktop button.active {
  color: var(--accent);
  background: var(--accent-soft);
}
/* Support/donate — sits apart from the tab list as a quiet call to action:
   accent text + a hairline pill, so it reads as "chip in", not another tab. */
.nav-desktop button.nav-support {
  color: var(--accent);
  background: var(--accent-soft);
  border: 1px solid rgba(247, 129, 102, 0.35);
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.nav-desktop button.nav-support:hover {
  background: rgba(247, 129, 102, 0.18);
  border-color: var(--accent);
}

/* Hamburger button — three lines that morph into an X on open. */
.nav-hamburger {
  display: none;
  width: 36px; height: 36px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0;
  position: relative;
  cursor: pointer;
}
.nav-hamburger span {
  position: absolute;
  left: 9px;
  width: 18px; height: 2px;
  background: var(--text);
  border-radius: 1px;
  transform-origin: center;
  transition: transform 0.22s ease, opacity 0.18s ease, top 0.22s ease;
}
.nav-hamburger span:nth-child(1) { top: 12px; }
.nav-hamburger span:nth-child(2) { top: 17px; }
.nav-hamburger span:nth-child(3) { top: 22px; }
.nav-hamburger.open span:nth-child(1) { top: 17px; transform: rotate(45deg); }
.nav-hamburger.open span:nth-child(2) { opacity: 0; }
.nav-hamburger.open span:nth-child(3) { top: 17px; transform: rotate(-45deg); }

/* Mobile drawer — full-width slide-down sheet. */
.nav-drawer-backdrop {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 99;
  animation: fade-in 0.2s ease;
}
@keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
.nav-drawer {
  display: none;
  position: fixed;
  top: 56px; /* sits below the header */
  left: 0; right: 0;
  background: var(--bg-elev);
  border-bottom: 1px solid var(--border);
  z-index: 100;
  flex-direction: column;
  padding: 12px;
  gap: 4px;
  transform: translateY(-110%);
  /* Two-track transition: transform animates the slide; visibility
     toggles instantaneously but only AFTER the slide-up completes when
     closing. Without `visibility: hidden`, even with translateY past
     -100%, the drawer's box-shadow paints into the viewport — the
     "phantom bar at the top" some users saw. With it, the entire
     drawer (including shadow) is removed from the layer tree when
     closed. `pointer-events` keeps clicks from registering on
     half-rendered content during the transition. */
  transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
              visibility 0s linear 0.22s;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
  visibility: hidden;
  pointer-events: none;
}
.nav-drawer.open {
  transform: none;
  visibility: visible;
  pointer-events: auto;
  transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
              visibility 0s linear 0s;
}
.nav-drawer button {
  background: transparent;
  border: none;
  text-align: left;
  font-size: 15px;
  font-weight: 500;
  padding: 14px 16px;
  border-radius: 8px;
  color: var(--text-dim);
  font-family: var(--font-body);
}
.nav-drawer button:hover, .nav-drawer button:focus {
  background: var(--bg-elev-2);
  color: var(--text);
  outline: none;
}
.nav-drawer button.nav-support { color: var(--accent); display: inline-flex; align-items: center; gap: 8px; }
.nav-drawer button.active {
  color: var(--accent);
  background: var(--accent-soft);
}

.body {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 24px;
  padding: 24px;
  flex: 1;
}
/* Gentle page-load fade-up for the main content panel. Pages that
   already use `.reveal` on hero elements get their own staggered
   reveals on top of this. Honors prefers-reduced-motion via the
   global rule in style.css (this is a custom keyframe, so we duplicate
   the override). */
.body main {
  min-width: 0;
  animation: main-fade 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
}
/* Opacity-only (no transform): a transform on <main> makes it the containing
   block for position:fixed/sticky descendants, which broke the Trends sticky
   bar. The fade alone reads the same. */
@keyframes main-fade {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .body main { animation: none; }
}
.body:has(main:only-child) { grid-template-columns: 1fr; }
aside {
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 16px;
  height: fit-content;
  position: sticky;
  /* Clear the ~53px sticky header (+ breathing room) so the top of the
     filter panel isn't hidden behind it once the user scrolls. */
  top: 72px;
  max-height: calc(100vh - 96px);
  overflow-y: auto;
}

footer {
  border-top: 1px solid var(--border);
  background: var(--bg-elev);
  padding: 18px 24px;
}
.footer-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text-dim);
}
/* Footer links use --text-dim with an editorial animated underline:
   a 1px line that grows from left on hover. Avoids the generic
   "blue link" look while still affording clear interactivity. */
.footer-inner a {
  color: var(--text-dim);
  text-decoration: none;
  background-image: linear-gradient(currentColor, currentColor);
  background-position: 0 100%;
  background-repeat: no-repeat;
  background-size: 0% 1px;
  padding-bottom: 1px;
  transition: color 0.15s ease, background-size 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}
.footer-inner a:hover {
  color: var(--text);
  background-size: 100% 1px;
}
/* Support link reads in the accent so it's the one footer item that pops. */
.footer-inner a.footer-support { color: var(--accent); font-weight: 600; display: inline-flex; align-items: center; gap: 5px; }
.footer-inner a.footer-support:hover { color: var(--accent); }
.sep { opacity: 0.5; }

/* Floating filters button — only renders on phones (the @media block
   below flips display on). Hidden by default so it never interferes
   with the always-visible desktop sidebar. */
.filters-fab { display: none; }
.filters-backdrop { display: none; }
/* In-drawer header (close button + title). Desktop never shows the
   drawer pattern, so this row is suppressed there — the sidebar is
   always inline beneath the page header. The mobile @media block
   re-enables it. */
.filters-aside-header { display: none; }

/* ─── Nav collapse breakpoint ───
   The inline desktop nav is wordmark + 8 tabs + unit toggle. That
   crowds and wraps well before the 768px layout breakpoint, so the
   hamburger takes over earlier (at 900px) while the two-column body
   layout below still holds until 768px. */
@media (max-width: 900px) {
  .nav-desktop { display: none; }
  .nav-hamburger { display: inline-block; }
  .nav-drawer { display: flex; }
  .nav-drawer-backdrop { display: block; }
}

/* ─── Mobile breakpoint ─── */
@media (max-width: 768px) {
  header {
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
  }
  .brand { flex: 1; min-width: 0; }
  .brand h1 { font-size: 15px; }
  .header-toggle { flex: 0 0 auto; margin-left: 0; }

  /* Floating action button. Anchored bottom-right with safe-area
     padding so it sits clear of the iOS home-indicator. Brand orange
     so it reads as the page's primary action — anyone who's scrolled
     deep into a chart can tap it without scrolling back up. */
  .filters-fab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    position: fixed;
    right: max(16px, env(safe-area-inset-right));
    bottom: max(16px, env(safe-area-inset-bottom));
    z-index: 1100;
    padding: 12px 18px;
    font-family: var(--font-mono);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #1c0e09;
    background: var(--accent);
    border: none;
    border-radius: 999px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45),
                0 0 0 2px rgba(247, 129, 102, 0.18);
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  }
  .filters-fab:hover { filter: brightness(1.08); }
  .filters-fab:active { transform: scale(0.97); }
  /* Drawer fully covers the FAB at our 92vw width — the FAB at the
     same z-index gets painted under the drawer and turns into a
     dead button. Hide it while the drawer is open; the in-drawer
     close button is the canonical close action instead. */
  .filters-fab.open { display: none; }
  .filters-fab svg { display: block; flex: 0 0 auto; }

  /* In-drawer header — sticky at the top of the scroll container so
     the close button is always reachable even after the user scrolls
     deep into the filter list. Style mirrors the ShareProfileModal
     header (mono small-caps title + borderless ×) for visual
     consistency across the site's overlay surfaces. */
  .filters-aside-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: -14px; /* counters the .filters-aside top padding so the
                   header sits flush against the drawer's top edge */
    margin: -14px -14px 10px;
    padding: 10px 14px;
    background: var(--bg-elev);
    border-bottom: 1px solid var(--border);
    z-index: 1;
  }
  .filters-aside-title {
    font-family: var(--font-mono);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text);
  }
  .filters-aside-close {
    background: transparent;
    border: none;
    color: var(--text-dim);
    font-size: 26px;
    line-height: 1;
    cursor: pointer;
    padding: 2px 8px;
    border-radius: 4px;
    transition: color 0.12s ease, background 0.12s ease;
  }
  .filters-aside-close:hover,
  .filters-aside-close:active {
    color: var(--accent);
    background: rgba(247, 129, 102, 0.08);
  }

  /* Backdrop — sits between the page content and the drawer. Tap
     anywhere outside the drawer to close. */
  .filters-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1090;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(2px);
    animation: filters-backdrop-in 0.18s ease-out;
  }
  @keyframes filters-backdrop-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  .body {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 12px 12px 96px; /* room at bottom so content isn't
                                obscured by the floating FAB */
  }
  /* Drawer geometry — slides in from the right edge. Always present
     in the DOM (display: flex) so the transition can animate; the
     transform keeps it off-screen until `.open`. */
  .filters-aside {
    position: fixed;
    top: 0;
    right: 0;
    /* iOS Safari treats `bottom: 0` as the LAYOUT viewport, which
       can extend behind the dynamic URL bar — the bottom rows of
       the drawer were sitting under the browser chrome. Use the
       dynamic viewport unit (`dvh`) so the drawer height tracks the
       *visible* viewport as the URL bar shows/hides. `vh` fallback
       keeps older WebKit working; the `dvh` line wins where supported. */
    height: 100vh;
    height: 100dvh;
    width: min(320px, 84vw);
    max-height: none;
    z-index: 1100;
    background: var(--bg-elev);
    border-left: 1px solid var(--border);
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.4);
    transform: translateX(100%);
    transition: transform 0.25s cubic-bezier(0.32, 0.72, 0.32, 1);
    overflow-y: auto;
    overscroll-behavior: contain;
    /* Bottom pad = safe-area inset (home indicator) + comfortable
       breathing room so the last filter row is never crammed against
       the bottom edge. The FAB is hidden while the drawer is open,
       so we don't need extra clearance for it specifically. */
    padding: 14px 14px calc(env(safe-area-inset-bottom, 0px) + 32px);
    -webkit-overflow-scrolling: touch;
  }
  .filters-aside.open { transform: translateX(0); }

  footer { padding: 12px; }
}

/* Reduced-motion: skip the slide and the backdrop fade. Drawer just
   appears/disappears. */
@media (max-width: 768px) and (prefers-reduced-motion: reduce) {
  .filters-aside { transition: none; }
  .filters-backdrop { animation: none; }
}
</style>
